import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/api/client";
import { ArrowLeft, Loader2, ShieldAlert, RefreshCcw, History } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

export default function AdminParticipantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/participants/${id}`);
      setData(data);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  useEffect(() => { load(); }, [id]);

  const grantException = async (granted) => {
    await api.post(`/admin/participants/${id}/activities-exception`, { granted, reason: "Excepción administrativa trazable" });
    setMsg(granted ? "Excepción concedida: puede acceder a actividades sin diagnóstico completado." : "Excepción retirada.");
    load();
  };

  const reopen = async () => {
    try {
      await api.post(`/admin/participants/${id}/diagnostic/reopen`, { reason: reopenReason || "Reapertura administrativa" });
      setReopenOpen(false); setReopenReason("");
      setMsg("Diagnóstico reabierto. El intento anterior quedó registrado en el historial. El dashboard se actualizará automáticamente.");
      load();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  if (error) return <div className="text-rose-600 text-sm font-semibold">{error}</div>;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />;

  const { participant: p, diagnostic, activities, attendance } = data;
  const reopenHistory = diagnostic?.reopen_history || [];

  return (
    <div className="space-y-6">
      <Link to="/admin/participantes" className="text-xs font-bold text-slate-500 hover:text-brand-accent flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Volver a matrícula</Link>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h1 className="text-xl font-black text-slate-800">{p.full_name}</h1>
        <p className="text-xs text-slate-500 mt-1">{p.rut_display} · {p.email} · {p.unit}</p>
        {msg && <p className="text-xs font-semibold text-emerald-600 mt-2" data-testid="admin-participant-msg">{msg}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="admin-participant-diagnostic">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black text-slate-800">Diagnóstico</h3>
          {diagnostic?.status === "completed" && (
            <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
              <AlertDialogTrigger asChild>
                <button data-testid="admin-reopen-diagnostic-btn" className="text-xs font-bold text-brand-accent flex items-center gap-1"><RefreshCcw className="w-3.5 h-3.5" /> Reabrir (función de soporte)</button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="admin-reopen-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Reabrir el diagnóstico de {p.full_name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta es una función EXCEPCIONAL de soporte. El participante podrá volver a responder el diagnóstico completo.
                    El intento anterior (respuestas y resultados) quedará registrado en el historial de trazabilidad, no se pierde.
                    Al finalizar la nueva respuesta, el dashboard y los resultados del participante se recalcularán automáticamente.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">Motivo de la reapertura (obligatorio para trazabilidad)</label>
                  <Textarea data-testid="admin-reopen-reason-input" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Ej: el participante reporta un error técnico durante el envío final." className="text-sm" />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="admin-reopen-cancel-btn">Cancelar</AlertDialogCancel>
                  <AlertDialogAction data-testid="admin-reopen-confirm-btn" disabled={!reopenReason.trim()} onClick={reopen}>Confirmar reapertura</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        {!diagnostic ? <p className="text-xs text-slate-400">Sin diagnóstico iniciado.</p> : diagnostic.status !== "completed" ? (
          <p className="text-xs text-amber-600 font-semibold">En progreso ({Object.keys(diagnostic.likert_responses || {}).length}/18 conductas, {Object.keys(diagnostic.case_responses || {}).length}/8 casos).</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 text-center">
            {diagnostic.results.dimensions.map((d) => (
              <div key={d.id} className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] font-bold text-slate-400">{d.id}</div><div className="text-sm font-black text-slate-800">{d.score}</div></div>
            ))}
            <div className="bg-brand-softBlue rounded-lg p-2"><div className="text-[10px] font-bold text-slate-500">IHD</div><div className="text-sm font-black text-brand-accent">{diagnostic.results.ihd}</div></div>
          </div>
        )}
        {reopenHistory.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="admin-reopen-history">
            <p className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1.5 mb-2"><History className="w-3 h-3" /> Historial de reaperturas</p>
            <ul className="space-y-1.5">
              {reopenHistory.map((h, i) => (
                <li key={i} className="text-[11px] text-slate-500">
                  <span className="font-semibold text-slate-700">{h.reopened_at ? new Date(h.reopened_at).toLocaleString("es-CL") : "—"}</span> · Motivo: {h.reason} · IHD anterior: <strong>{h.snapshot?.results?.ihd ?? "—"}</strong>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="admin-participant-activities">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black text-slate-800">Actividades</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => grantException(!p.activities_exception?.granted)} className="text-xs font-bold text-amber-600 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> {p.activities_exception?.granted ? "Retirar excepción" : "Conceder excepción"}</button>
          </div>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-400 uppercase text-[10px] border-b"><th className="py-2">Semana</th><th>Estado</th><th>Fecha envío</th></tr></thead>
          <tbody>
            {[1,2,3,4,5].map((w) => {
              const sub = activities.find((a) => a.week === w);
              return (
                <tr key={w} className="border-b border-slate-100">
                  <td className="py-2">{w}</td>
                  <td>{sub ? sub.status : "sin registro"}</td>
                  <td>{sub?.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-800 mb-4">Asistencia</h3>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-400 uppercase text-[10px] border-b"><th className="py-2">Semana</th><th>Estado</th></tr></thead>
          <tbody>
            {[1,2,3,4,5].map((w) => {
              const att = attendance.find((a) => a.week === w);
              return <tr key={w} className="border-b border-slate-100"><td className="py-2">{w}</td><td>{att ? att.status : "no_registrada"}</td></tr>;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
